import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Self-service password change card. Renders identically on Customer, Vendor, Admin and
 * Agent dashboards so users always know where to update credentials. POSTs to
 * `/users/me/password` which is role-agnostic and verifies the current password before
 * accepting the new hash.
 */
export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!current || !next) { toast.error("Current and new password are required"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation do not match"); return; }
    setSaving(true);
    try {
      await api.post("/users/me/password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-white p-6 mb-8" data-testid="change-password-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-2xl bg-amber-50 flex items-center justify-center"><KeyRound className="h-4 w-4 text-amber-700" /></div>
        <div>
          <h2 className="font-display text-lg font-medium">Change password</h2>
          <p className="text-xs text-neutral-500">At least 6 characters. We never display your current password.</p>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
        <div>
          <Label>Current password</Label>
          <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 rounded-xl" data-testid="password-current" />
        </div>
        <div>
          <Label>New password</Label>
          <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 rounded-xl" data-testid="password-new" />
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-xl" data-testid="password-confirm" />
        </div>
        <div className="md:col-span-3">
          <Button type="submit" disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="password-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Update password
          </Button>
        </div>
      </form>
    </div>
  );
}
